import {
  addDays,
  addYears,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  format,
  getDay,
  parse,
  setDay,
  startOfMonth,
} from 'date-fns';

import { getStartEndFromMergedYmdLists } from '../domain/chain/date';

import { ReportsDateUnit } from './reportsDateRange';

/**
 * カスタム検索タイプ（範囲選択・自由選択）で使用するSIS日付のダミー値
 * APIでハイフンを返すための値であり、enableSisで設定される
 * 関連ドキュメント
 * [チェーン店レポートのsisDateRangeTypeが検索タイプがカスタムの場合にdailyになってしまう件の調査](https://www.notion.so/sisDateRangeType-daily-2eaa4e41249f803f9bcdd3eb9d4f6b8d)
 * [[チェーン店レポート] カスタムの範囲で検索時にDK-SISのデータを含めた場合のレスポンスについての問題](https://www.notion.so/DK-SIS-1c0a4e41249f8194a549d352f2781193?pvs=25)
 */
export const SIS_CUSTOM_SEARCH_DUMMY_DATE = '0000-01-01';

type BuildSisDateRangeForChainStoreParams = {
  startDate?: string;
  endDate?: string;
  sisStartDate?: string;
  sisEndDate?: string;
  ymdList?: string[];
};

type BuildSisDateRangeForChainStoreSearchCondition = {
  ymdList?: string[];
  ymdComparisonList?: string[];
};

/**
 * チェーン店レポート用のSIS日付範囲を組み立てる。
 * - カスタム検索の場合はDUMMYを返しSISを表示しない。
 * - 平日/土日祝比較の場合は検索条件のymdListとymdComparisonListをマージした範囲を使用する。
 * - その他の場合はparamsの日付を使用する。
 *
 * @param dateUnit 期間単位（日/週/月/カスタム）
 * @param comparativeSection 比較区分（平日/土日祝比較の判定に使用。undefinedの場合はその他扱い）
 * @param searchCondition 検索条件（ymdList: 平日, ymdComparisonList: 土日祝）
 * @param params フォールバック用の日付・SIS日付
 * @returns sisStartDate, sisEndDate（sagaでspreadして使用）
 */
export const buildSisDateRangeForChainStore = (
  dateUnit: ReportsDateUnit,
  comparativeSection: string | undefined,
  searchCondition: BuildSisDateRangeForChainStoreSearchCondition,
  params: BuildSisDateRangeForChainStoreParams
): { sisStartDate: string | undefined; sisEndDate: string | undefined } => {
  // SIS日付範囲のパターンを判定する
  const sisDateRangePattern =
    toSisDateRangeType(dateUnit) === 'custom'
      ? 'custom'
      : comparativeSection === '平日/土日祝比較'
      ? 'weekdayHoliday'
      : 'default';

  switch (sisDateRangePattern) {
    // カスタム検索の場合はDUMMYを返しSISを表示しない。
    case 'custom':
      return {
        sisStartDate: params.sisStartDate ?? SIS_CUSTOM_SEARCH_DUMMY_DATE,
        sisEndDate: params.sisEndDate ?? SIS_CUSTOM_SEARCH_DUMMY_DATE,
      };
    case 'weekdayHoliday': {
      // 平日/土日祝比較の場合は検索条件のymdListとymdComparisonListをマージした範囲を使用する。
      const merged = getStartEndFromMergedYmdLists(
        searchCondition.ymdList,
        searchCondition.ymdComparisonList,
        { startDate: params.startDate, endDate: params.endDate }
      );
      return {
        sisStartDate: merged.startDate ?? params.sisStartDate,
        sisEndDate: merged.endDate ?? params.sisEndDate,
      };
    }
    default:
      // その他の場合はparamsの日付を使用する。
      return {
        sisStartDate:
          params.sisStartDate ?? params.ymdList?.at(0) ?? params.startDate,
        sisEndDate:
          params.sisEndDate ?? params.ymdList?.at(-1) ?? params.endDate,
      };
  }
};

/**
 * 最新の週の日付を算出する
 * @param latestDate DK-SISの最新データの週の開始日
 * @returns 最新の週の開始日と終了日
 */
export const latestWeeklyOption = (latestDate: string) => {
  try {
    const rowDate = parse(latestDate, 'yyyy-MM-dd', new Date());
    const endDate = addDays(rowDate, 6);

    return {
      sisStartDate: latestDate,
      sisEndDate: format(endDate, 'yyyy-MM-dd'),
    };
  } catch (e) {
    return {
      sisStartDate: '',
      sisEndDate: '',
    };
  }
};

/**
 * 最新の月の日付を算出する
 * @param latestDate DK-SISの最新データの月の開始日
 * @returns 最新の月の開始日と終了日
 */
export const latestMonthlyOption = (latestDate: string) => {
  try {
    const rowDate = parse(latestDate, 'yyyy-MM-dd', new Date());
    const startDate = startOfMonth(rowDate);
    const endDate = endOfMonth(rowDate);

    return {
      sisStartDate: format(startDate, 'yyyy-MM-dd'),
      sisEndDate: format(endDate, 'yyyy-MM-dd'),
    };
  } catch (e) {
    return {
      sisStartDate: '',
      sisEndDate: '',
    };
  }
};

/**
 * 週の期間の選択肢を1年分生成する
 * @param latestDate DK-SISの最新データの週の開始日
 * @returns 一年分の週の期間の選択肢
 */
export const getWeeklyOptions = (latestDate: string) => {
  try {
    const rowDate = parse(latestDate, 'yyyy-MM-dd', new Date());

    const day = getDay(rowDate); // 曜日
    const lastStartDate = setDay(addYears(rowDate, -1), day, {
      weekStartsOn: day,
    }); // 1年後の同じ曜日
    const latestEndDate = addDays(rowDate, 6); // 開始日の週の週末

    // 開始日の一覧
    const weekList = eachWeekOfInterval(
      {
        start: lastStartDate,
        end: latestEndDate,
      },
      {
        weekStartsOn: day,
      }
    ).reverse();

    // 週の期間の選択肢
    const weeklyOptions = weekList.map((date, idx) => {
      const week = latestWeeklyOption(format(date, 'yyyy-MM-dd'));
      return {
        id: idx,
        ...week,
      };
    });

    return weeklyOptions;
  } catch (e) {
    return [];
  }
};

/**
 * 月の期間の選択肢を1年分生成する
 * @param latestDate DK-SISの最新データの月の開始日
 * @returns 一年分の月の期間の選択肢
 */
export const getMonthlyOptions = (latestDate: string) => {
  try {
    const rowDate = parse(latestDate, 'yyyy-MM-dd', new Date());

    const lastStartDate = startOfMonth(addYears(rowDate, -1)); // 1年後の月の初日
    const latestEndDate = endOfMonth(rowDate); // 開始日の月の月末

    // 開始日の一覧
    const monthList = eachMonthOfInterval({
      start: lastStartDate,
      end: latestEndDate,
    }).reverse();

    // 月の期間の選択肢
    const monthlyOptions = monthList.map((date, idx) => {
      const month = latestMonthlyOption(format(date, 'yyyy-MM-dd'));
      return {
        id: idx,
        ...month,
      };
    });

    return monthlyOptions;
  } catch (e) {
    return [];
  }
};

/**
 * ReportsDateUnitからsisDateRangeTypeに変換する
 */
export const toSisDateRangeType = (from: ReportsDateUnit) => {
  switch (from) {
    case '日':
      return 'daily' as const;
    case '週':
      return 'weekly' as const;
    case '月':
      return 'monthly' as const;
    default:
      return 'custom' as const;
  }
};

/**
 * sis系のパラメータをfieldsから除外する
 *
 * お気に入りでfieldsにsis系の値を含む場合があるのですべて除外する必要がある
 * sis系のパラメータが正しく付与されていればフロントからこれらのfieldsを指定する必要はない
 * ただしこの処理を展開行に使用しないでください、必要な列データもomitされます
 * 展開行でsis系データを含めることは使用上許容されています
 * https://www.notion.so/DK-SIS-1c0a4e41249f814b9ccede64be3d367e
 */
export const omitSisFields = (
  fields: string[] | undefined,
  fieldCodes: string[]
) => {
  if (fields == null) {
    return undefined;
  }

  return fields.filter((field) => fieldCodes.includes(field));
};

/**
 * DK-SISを有効にするようにパラメータを変更する
 */
export const enableSis = (
  ymdList: string[] | undefined,
  dateUnit: ReportsDateUnit
) => {
  const sisDateRangeType = toSisDateRangeType(dateUnit);

  const defaultValue = {
    sisFieldTypes: ['result'],
    sisStartDate: ymdList?.at(0),
    sisEndDate: ymdList?.at(-1),
  };

  if (sisDateRangeType === 'custom') {
    return {
      ...defaultValue,
      // 何かしらの値がないとエラーになるためダミーで入力
      // この値はAPIでエラーにならない値であればなんでもよい
      sisDateRangeType: 'daily' as const,
      sisStartDate: SIS_CUSTOM_SEARCH_DUMMY_DATE,
      sisEndDate: SIS_CUSTOM_SEARCH_DUMMY_DATE,
    };
  }

  return {
    ...defaultValue,
    sisDateRangeType,
  };
};

/**
 * DK-SISを無効にするようにパラメータを変更する
 */
export const disableSis = (
  fields: string[] | undefined,
  fieldCodes: string[]
) => {
  return {
    // fieldsにsis系が含まれていた場合に除外
    fields: omitSisFields(fields, fieldCodes),
    sisFieldTypes: undefined,
    sisStartDate: undefined,
    sisEndDate: undefined,
    sisDateRangeType: undefined,
  };
};
